import { getSenderNameFromMessage } from '@/utils/variantBasedLabels';
import { Message } from '@/models/Message';
import { MessageFeedback } from '@/components/chat/MessageFeedback';
import { MessageTimestamp } from '@/components/chat/MessageTimestamp';
import { mkUserNameTrackAttrs } from '@/analytics/props/msg';
import { MULTI_TOOL, TABLE_ANNOTATION } from 'configs/NoraVariants';
import { optAppConfig } from '@/utils/config';
import { PermissionsContext } from '@/components/context/PermissionsContext';
import { renderMessage } from '@/utils/RenderMessageUtils';
import { UserLabel } from '@/components/chat/UserLabel';
import ToolsTooltip from '@/components/chat/ToolsTooltip';

import { styled } from '@mui/material';
import { useContext } from 'react';

export interface MessageContentProps {
  message: Message;
}

export const MessageContent = ({ message }: MessageContentProps) => {
  const { isReadOnly } = useContext(PermissionsContext);
  const noraExperienceVariant = optAppConfig((_) => _.experience.variant);
  const shouldRenderFeedback =
    noraExperienceVariant === MULTI_TOOL ||
    noraExperienceVariant === TABLE_ANNOTATION;
  const nameLabel = getSenderNameFromMessage(message);

  return (
    <Layout>
      <LayoutDisplayName>
        <UserLabel
          {...mkUserNameTrackAttrs({
            messageId: message.id,
            userId: message.sender.uuid,
          })}
        >
          {nameLabel}
        </UserLabel>
      </LayoutDisplayName>
      <LayoutTimestamp>
        <MessageTimestamp message={message} />
      </LayoutTimestamp>
      <LayoutMessage>
        <MessageWrap>{renderMessage(message)}</MessageWrap>
      </LayoutMessage>
      {shouldRenderFeedback && (
        <LayoutActions>
          {isReadOnly ? (
            message.hasToolCalls && <ToolsTooltip messageId={message.id} />
          ) : (
            <MessageFeedback message={message} />
          )}
        </LayoutActions>
      )}
    </Layout>
  );
};

const Layout = styled('div')`
  display: grid;
  gap: ${({ theme }) => theme.spacing(6 / 8)};
  grid-template-areas:
    'displayName timestamp'
    'message message'
    'actions actions';
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
`;

const LayoutDisplayName = styled('div')`
  grid-area: displayName;
`;

const LayoutTimestamp = styled('div')`
  grid-area: timestamp;
`;

const LayoutMessage = styled('div')`
  grid-area: message;
`;

const LayoutActions = styled('div')`
  grid-area: actions;
`;

const MessageWrap = styled('div')`
  hyphens: auto;
  overflow-wrap: break-word;
`;
